/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, MouseEvent, useCallback, useState} from 'react';
import {Collapse, List, ListItem} from '@material-ui/core';
import classnames from 'classnames';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {MaterialExpandLessIcon} from '../../icons/MaterialExpandLessIcon';
import {MaterialExpandMoreIcon} from '../../icons/MaterialExpandMoreIcon';
import {IconButton} from '../buttons/IconButton';
import CheckBox from '../checkbox/CheckBox';
import {AutocompleteSearchResult} from '../search/AutocompleteSearchResult';
import {useStyles} from './styles';

export interface TreeViewData {
  id: string;
  name: string;
  displayName?: string;
  children?: TreeViewData[];
  searchableString?: string;
}

interface ExpandableDynamicTreeProps {
  expandOnOptionClick?: boolean;
}

export interface DynamicTreeViewProps extends ExpandableDynamicTreeProps {
  data: TreeViewData[];
  onItemClick?: (data: TreeViewData) => void;
  multiselect?: boolean;
  useCheckBox?: boolean;
  parentClick?: boolean;
  isExandedOnLoad?: boolean;
  textClicked?: boolean;
  searchText?: string;
}

export interface TreeElementProps extends ExpandableDynamicTreeProps {
  item: TreeViewData;
  classes: any;
  useCheckBox?: boolean;
  onItemClick?: (data: TreeViewData) => void;
  depth?: number;
  parentClick?: boolean;
  isExandedOnLoad?: boolean;
  rootIndex?: string;
  textClicked?: boolean;
  searchText?: string;
}

/**
 * Renders dynamic tree Item
 *
 * ```typescript
 * import {TreeElement} from '@ion/dls/src/components/treeviews';
 *
 *  <DynamicTreeView item={treeListItem}
 *   useCheckBox={true}
 *   ariaLabel={'any string'}
 *   depth={0}
 *   isExandedOnLoad={false}
 *   rootIndex={1}
 *   onItemClick={onLabelSelect}/>
 * ```
 *
 * @param item Object to be used as treeview element
 * @param useCheckbox boolean for display checkbox
 * @param depth indicates the tree structure hierarchial depth
 * @param isExpandedOnLoad defaults to false but will expand on render if set to true
 * @param rootIndex index carried over on every children iteration to trace back to the root level path
 */
export const TreeElement = ({
  item,
  classes,
  useCheckBox,
  searchText,
  textClicked,
  onItemClick,
  depth = 0,
  isExandedOnLoad = false,
  rootIndex,
  expandOnOptionClick = false,
  parentClick = false
}: TreeElementProps) => {
  const [showMore, canShowMore] = useState<boolean>(isExandedOnLoad);
  const {name, id, displayName, children} = item;
  const onExpandClick = useCallback(function(e: MouseEvent<HTMLElement>) {
    e.stopPropagation();
    canShowMore(!showMore);
  }, [showMore]);
  const onClick = useCallback(function(e: MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    if (!parentClick) {
      !item?.children && onItemClick(item);
      if (expandOnOptionClick) {
        onExpandClick(e);
      }
    } else {
      onItemClick(item);
    }
  }, [item, parentClick, showMore]);
  const onCheckBoxClick = useCallback(function(e: ChangeEvent<{name?: string; value: unknown;}>) {
    e?.preventDefault();
    e?.stopPropagation();
  }, []);
  return (
    <ListItem classes={{root: classes.treeItemContainer}} style={{marginLeft: depth*32}}>
      <Collapse classes={{root: classes.collapseContainer}}
        in={showMore || textClicked}
        collapsedSize={40}>
        <div role="button"
          aria-pressed="false"
          tabIndex={1}
          onClick={onClick}
          className={classnames(classes.treeItemLabel, {[classes.paddedListElement]: useCheckBox})}>
          {children?.length > 0 && (<IconButton disableRipple
            disableHover={true}
            shouldWrap={true}
            onClick={onExpandClick}
            icon={showMore || textClicked ? <MaterialExpandLessIcon /> : <MaterialExpandMoreIcon />} />
          )}
          {useCheckBox && (<CheckBox checked={true} onChange={onCheckBoxClick} label={''} />)}
          <AutocompleteSearchResult option={{name: displayName ?? name}}
            searchValue={searchText}
            highlightColor={dlsColor('lightBlue')} />
        </div>
        {children && (
          <List classes={{root: classes.listContainer}}>
            {children?.map((child: TreeViewData, index: number) => (
              <TreeElement key={`${index}-${child.name}-${name}`}
                depth={depth+1}
                searchText={searchText}
                item={child}
                useCheckBox={useCheckBox}
                classes={classes}
                rootIndex={`${rootIndex}.children.${index}`}
                onItemClick={onItemClick} />
            ))}
          </List>
        )}
      </Collapse>
    </ListItem>
  );
};

/**
 * Renders dynamic tree data
 *
 * ```typescript
 * import {DynamicTreeView} from '@ion/dls/src/components/treeviews';
 *
 *  <DynamicTreeView data={data}
 *   ariaLabel={'any string'}
 *   onItemClick={onLabelSelect}/>
 * ```
 */
export const DynamicTreeView = ({
  data,
  onItemClick,
  textClicked,
  searchText,
  expandOnOptionClick = false,
  multiselect = false,
  useCheckBox = false,
  parentClick = false,
  isExandedOnLoad = false
}: DynamicTreeViewProps) => {
  const classes = useStyles();

  return (
    <>
      {data?.map((item: TreeViewData, index: number) => (
        <TreeElement key={`${index}-${item.name}`}
          item={item}
          expandOnOptionClick={expandOnOptionClick}
          parentClick={parentClick}
          onItemClick={onItemClick}
          useCheckBox={useCheckBox}
          textClicked={textClicked}
          searchText={searchText}
          classes={classes}
          rootIndex={`${index}`}
          isExandedOnLoad={isExandedOnLoad}/>
      ))}
    </>
  );
};
